import React, { useEffect, useRef, useState } from 'react';
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonIcon,
  IonContent,
  IonCard,
  IonCardContent,
  IonSpinner,
  IonText,
} from '@ionic/react';
import { closeOutline } from 'ionicons/icons';
import styles from './HelcimCheckoutModal.module.scss';
import { useTranslation } from 'react-i18next';

// Ported from public/pay.html (the customer-facing checkout page) — same backend
// endpoint and same HelcimPay.js embedding mechanics, just triggered from inside
// the app by staff instead of via an emailed link.
const HELCIM_PAY_SCRIPT_SRC = 'https://secure.helcim.app/helcim-pay/services/start.js';
const CHECKOUT_FUNCTION_URL = 'https://us-central1-rsmaidsbooking.cloudfunctions.net/helcimCheckout';

declare global {
  interface Window {
    appendHelcimPayIframe?: (checkoutToken: string) => void;
  }
}

let scriptLoadPromise: Promise<void> | null = null;
function loadHelcimPayScript(): Promise<void> {
  if (scriptLoadPromise) return scriptLoadPromise;
  scriptLoadPromise = new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${HELCIM_PAY_SCRIPT_SRC}"]`)) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = HELCIM_PAY_SCRIPT_SRC;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load HelcimPay.js'));
    document.body.appendChild(script);
  });
  return scriptLoadPromise;
}

interface HelcimCheckoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  amount: number;
  invoiceNumber?: string;
  customerCode?: string;
  onSuccess?: () => void;
}

const HelcimCheckoutModal: React.FC<HelcimCheckoutModalProps> = ({
  isOpen,
  onClose,
  amount,
  invoiceNumber,
  customerCode,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const checkoutTokenRef = useRef('');

  // Resetting to the loading state for each new checkout attempt is
  // computed during render (React's "adjust state when a dependency
  // changes" pattern) rather than as synchronous setState calls at the top
  // of the effect, which now only handles the actual async checkout flow.
  const [prevCheckoutKey, setPrevCheckoutKey] = useState({ isOpen, amount, invoiceNumber, customerCode });
  if (
    prevCheckoutKey.isOpen !== isOpen ||
    prevCheckoutKey.amount !== amount ||
    prevCheckoutKey.invoiceNumber !== invoiceNumber ||
    prevCheckoutKey.customerCode !== customerCode
  ) {
    setPrevCheckoutKey({ isOpen, amount, invoiceNumber, customerCode });
    if (isOpen) {
      setStatus('loading');
      setErrorMessage('');
    }
  }

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;

    const start = async () => {
      try {
        await loadHelcimPayScript();
        const res = await fetch(CHECKOUT_FUNCTION_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            amount,
            currency: 'USD',
            ...(invoiceNumber ? { invoiceNumber } : {}),
            ...(customerCode ? { customerCode } : {}),
          }),
        });
        const data = await res.json();
        if (cancelled) return;
        if (!res.ok || !data.checkoutToken) {
          throw new Error(data.message || data.error || t('components.HelcimCheckoutModal.failedToInitializePayment'));
        }
        checkoutTokenRef.current = data.checkoutToken;
        if (window.appendHelcimPayIframe) {
          window.appendHelcimPayIframe(data.checkoutToken);
          setStatus('ready');
        } else {
          throw new Error('HelcimPay.js did not load correctly.');
        }
      } catch (err) {
        if (cancelled) return;
        setErrorMessage(err instanceof Error ? err.message : t('components.HelcimCheckoutModal.somethingWentWrong'));
        setStatus('error');
      }
    };

    start();

    return () => {
      cancelled = true;
    };
  }, [isOpen, amount, invoiceNumber, customerCode, t]);

  useEffect(() => {
    if (!isOpen) return;

    const handleMessage = (event: MessageEvent) => {
      const token = checkoutTokenRef.current;
      if (!token || event.data?.eventName !== `helcim-pay-js-${token}`) return;

      // A terminal Helcim event arrived — remove the overlay iframe it injected.
      document.getElementById('helcimPayIframe')?.remove();

      const { eventStatus } = event.data;
      if (eventStatus === 'SUCCESS') {
        onSuccess?.();
        onClose();
      } else if (eventStatus === 'ABORTED') {
        onClose();
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [isOpen, onClose, onSuccess]);

  const handleClose = () => {
    document.getElementById('helcimPayIframe')?.remove();
    onClose();
  };

  return (
    <IonModal isOpen={isOpen} onDidDismiss={handleClose} className={styles.checkoutModal}>
      <IonHeader className="ion-no-border">
        <IonToolbar className={styles.toolbar}>
          <IonTitle>{t('components.HelcimCheckoutModal.directPay')}</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={handleClose}>
              <IonIcon slot="icon-only" icon={closeOutline} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className={styles.content}>
        {status === 'loading' && (
          <IonCard className={styles.stateCard}>
            <IonCardContent className={styles.stateCardContent}>
              <IonSpinner name="dots" />
              <IonText>{t('components.HelcimCheckoutModal.loadingSecurePaymentForm')}</IonText>
            </IonCardContent>
          </IonCard>
        )}
        {status === 'error' && (
          <IonCard className={styles.stateCard}>
            <IonCardContent className={styles.stateCardContent}>
              <IonText color="danger">{errorMessage}</IonText>
            </IonCardContent>
          </IonCard>
        )}
        {/* status === 'ready': HelcimPay.js injects its own full-viewport payment
            iframe onto document.body — nothing else to render here. */}
      </IonContent>
    </IonModal>
  );
};

export default HelcimCheckoutModal;
